/**
 * ImpersonationBanner component.
 *
 * Phase 3.1 Plan 03 (per ADR-006 + ADR-005):
 *   Persistent strip rendered above the TopNav while a staff user is
 *   impersonating a client org. Reads user.target_org from useAuth() for
 *   the display label. The Exit button calls useAuth().exitImpersonation
 *   which closes the server-side session, restores staff tokens, refetches
 *   /v1/auth/me, and triggers ImpersonationRedirect to navigate to
 *   /admin/fleet via its falling-edge detector.
 *
 *   Layout:
 *     - 40px strip above TopNav (AppShell renders it as the first child
 *       of shellRoot; CSS handles ordering).
 *     - Amber background to signal elevated state.
 *     - "Viewing as <target_org_name>" left-aligned with mono org name.
 *     - Exit button right-aligned; ghost style; cyan focus ring on Tab.
 *
 *   Accessibility:
 *     - role="status"
 *     - aria-live="polite" so SR announces on appear without interrupting
 *     - aria-label="Staff impersonation active"
 *
 *   Motion:
 *     - 140ms opacity-in transition on first mount; gated by
 *       useReducedMotion. Reduced-motion users see instant appear.
 *     - Banner never shifts layout after mount; AppShell reserves the slot
 *       when impersonating_as=true.
 *
 *   Defensive guards:
 *     - If user is null (impossible if AppShell only renders the banner
 *       on impersonating_as=true) the component returns null.
 *     - If target_org is missing (also impossible per the impersonation
 *       JWT shape) the component falls back to "another organization" so
 *       the user can still exit.
 */
import { useState } from 'react';
import { useAuth } from '@/features/auth/useAuth';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import {
  bannerRoot,
  bannerWithMotion,
  bannerLabel,
  bannerPrefix,
  bannerOrgName,
  bannerExitButton,
} from './ImpersonationBanner.css';

/**
 * Banner shown when user.impersonating_as is true.
 *
 * @returns The banner JSX, or null if user/context is in an impossible state.
 */
export function ImpersonationBanner() {
  const { user, exitImpersonation } = useAuth();
  const reducedMotion = useReducedMotion();
  const [exiting, setExiting] = useState(false);

  // Defensive: AppShell already gates rendering on user.impersonating_as,
  // but null-check anyway so this component is safe to render in isolation
  // (e.g., Storybook, unit tests with partial mocks).
  if (!user) {
    return null;
  }

  const orgName = user.target_org?.name ?? 'another organization';

  /**
   * Click handler for the Exit button.
   *
   * Disables the button while the exit flow is in flight to prevent
   * double-clicks. ImpersonationRedirect (mounted in AppShell) handles
   * the navigation back to /admin/fleet on the falling-edge transition.
   */
  const handleExit = async (): Promise<void> => {
    if (exiting) return;
    setExiting(true);
    try {
      await exitImpersonation();
    } catch (err) {
      // Phase 3.1 ships console.error; Phase 7 will swap to a Radix toast.
      // eslint-disable-next-line no-console
      console.error('exitImpersonation failed', err);
      // Keep the button enabled so the user can retry.
      setExiting(false);
    }
    // On success ImpersonationRedirect will fire and navigate away; the
    // component unmounts, so we don't bother resetting `exiting`.
  };

  // Compose the className: always bannerRoot, plus bannerWithMotion when
  // motion is allowed.
  const rootClass = reducedMotion
    ? bannerRoot
    : `${bannerRoot} ${bannerWithMotion}`;

  return (
    <div
      className={rootClass}
      role="status"
      aria-live="polite"
      aria-label="Staff impersonation active"
    >
      <div className={bannerLabel}>
        <span className={bannerPrefix}>Viewing as</span>
        <span className={bannerOrgName}>{orgName}</span>
      </div>
      <button
        type="button"
        className={bannerExitButton}
        onClick={() => {
          void handleExit();
        }}
        disabled={exiting}
      >
        {exiting ? 'Exiting...' : 'Exit'}
      </button>
    </div>
  );
}
